/**
 * Captura y persistencia de UTMs para la atribución de leads.
 *
 * ¿Por qué hace falta persistirlos?
 * Los UTMs sólo viven en la URL de aterrizaje. En flujos de varios pasos —como
 * `/vende-tu-auto-cotizador/*`— algunas navegaciones no arrastran el query
 * string, así que para cuando el usuario llega a "cita" u "ofertas" los UTMs ya
 * se perdieron y el lead sale sin atribución. Guardarlos al primer contacto
 * permite adjuntarlos a cualquier lead posterior del mismo recorrido.
 *
 * Se usa `sessionStorage` y no `localStorage` a propósito: vive por pestaña y
 * muere al cerrarla. Una visita orgánica en una pestaña nueva nace sin nada
 * guardado, de modo que nunca hereda la campaña de una sesión anterior. Con
 * `localStorage` un lead orgánico de la semana siguiente se atribuiría por error
 * a una campaña vieja.
 *
 * Tres reglas evitan los falsos positivos de atribución:
 *  1. Caducidad: lo guardado se ignora pasadas `UTM_TTL_MS`.
 *  2. Último toque gana: si llega una URL con UTMs nuevos, reemplazan a los
 *     anteriores (mismo criterio que Google Ads y GA4).
 *  3. Nunca se inventan campos: sin UTMs, el lead viaja sin esas llaves, no con
 *     cadenas vacías. Así el CRM distingue "orgánico" de "campaña sin valor".
 */

/**
 * Parámetros de campaña que se capturan. Es la lista canónica del proyecto:
 * `hooks/useUtms` la reutiliza para propagarlos entre páginas.
 */
export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
]

/** Llave bajo la que se guarda el registro en `sessionStorage`. */
const STORAGE_KEY = 'clikauto.utm'

/**
 * Ventana de validez de la atribución.
 *
 * 24 h cubre el caso habitual de "vuelvo más tarde a terminar la cotización"
 * sin que una pestaña olvidada durante días siga acreditando la campaña.
 * Para hacerla más estricta basta con bajar este valor.
 */
export const UTM_TTL_MS = 24 * 60 * 60 * 1000

/**
 * Devuelve `sessionStorage` sólo si es utilizable.
 *
 * No basta con comprobar que exista: en navegación privada o con el bloqueo de
 * datos de sitio activado, el simple acceso puede lanzar una excepción. La
 * atribución es un extra, así que ante cualquier fallo se degrada en silencio.
 */
const getStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    return window.sessionStorage
  } catch (e) {
    return null
  }
}

/**
 * Extrae los UTMs presentes en un query string.
 *
 * @param {string} search Query string con o sin "?" inicial.
 * @returns {object} Sólo las llaves de `UTM_KEYS` que vengan con valor.
 */
export const parseUtms = (search = '') => {
  const clean = `${search}`.replace(/^\?/, '')
  if (!clean) return {}

  const params = new URLSearchParams(clean)

  return UTM_KEYS.reduce((acc, key) => {
    const value = params.get(key)
    // Se ignoran las llaves ausentes y las vacías (`utm_source=`), que no
    // aportan atribución y sólo ensuciarían el CRM.
    if (value) acc[key] = value
    return acc
  }, {})
}

/**
 * Guarda los UTMs de la URL actual, si los hay.
 *
 * Si la URL no trae UTMs no se toca lo ya almacenado: así el usuario puede
 * navegar por páginas sin campaña sin perder la atribución de su entrada.
 *
 * @param {string} search Query string de la URL actual.
 * @returns {object} Los UTMs capturados en esta llamada (`{}` si no había).
 */
export const captureUtms = (search = '') => {
  const utms = parseUtms(search)
  if (Object.keys(utms).length === 0) return {}

  const storage = getStorage()
  if (!storage) return utms

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({
      values: utms,
      // Marca de tiempo para aplicar la caducidad al leer.
      capturedAt: Date.now(),
    }))
  } catch (e) {
    // Cuota llena o storage bloqueado: se sigue sin persistir.
  }

  return utms
}

/**
 * Lee los UTMs guardados que sigan vigentes.
 *
 * @returns {object} Los UTMs almacenados, o `{}` si no hay, caducaron o el
 *                   storage no está disponible.
 */
export const getStoredUtms = () => {
  const storage = getStorage()
  if (!storage) return {}

  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) return {}

    const { values, capturedAt } = JSON.parse(raw)
    if (!values || !capturedAt) return {}

    // Fuera de la ventana de atribución: se descarta y se limpia.
    if (Date.now() - capturedAt > UTM_TTL_MS) {
      storage.removeItem(STORAGE_KEY)
      return {}
    }

    return values
  } catch (e) {
    // Contenido corrupto o storage inaccesible: se trata como si no hubiera.
    return {}
  }
}

/** Borra la atribución guardada. Pensado para pruebas y depuración. */
export const clearStoredUtms = () => {
  const storage = getStorage()
  if (!storage) return
  try {
    storage.removeItem(STORAGE_KEY)
  } catch (e) {
    // Sin storage no hay nada que limpiar.
  }
}
